import React, { useState, useEffect, useContext } from "react";
import TopPanel from "./TopPanel";
import Sidebar from "./Sidebar";
import db from "./Firebase";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalContext from "./context/GlobalContext";
import { handleLogout } from "./Utility";

const COLLECTION = "product_categories";
// Only letters, numbers and spaces are allowed (no special characters)
const VALID_CATEGORY_REGEX = /^[\p{L}\p{N} ]+$/u;
const SPECIAL_CHAR_ERROR = "Category name cannot contain special characters";

const SubProducts = () => {
  const { permissible_roles } = useContext(GlobalContext);
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Same access check as Products page
  useEffect(() => {
    const loggedIn = localStorage.getItem("loggedIn") === "true";
    if (!loggedIn) {
      navigate("/login");
    } else if (permissible_roles.length > 0 && !permissible_roles.includes("products")) {
      handleLogout();
      navigate("/permission_denied");
    }
  }, [navigate, permissible_roles]);

  // Live list of categories
  useEffect(() => {
    const unsubscribe = db
      .collection(COLLECTION)
      .orderBy("name")
      .onSnapshot(
        (snap) => setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        (err) => {
          console.error("Error fetching categories: ", err);
          toast.error("Failed to load categories");
        }
      );
    return () => unsubscribe();
  }, []);

  const resetForm = () => {
    setCategoryName("");
    setEditId(null);
    setErrMsg("");
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setCategoryName(value);
    if (value && !VALID_CATEGORY_REGEX.test(value)) {
      setErrMsg(SPECIAL_CHAR_ERROR);
    } else {
      setErrMsg("");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const name = categoryName.trim();
    if (!name) {
      toast.error("Category name is required");
      return;
    }

    if (!VALID_CATEGORY_REGEX.test(name)) {
      setErrMsg(SPECIAL_CHAR_ERROR);
      toast.error(SPECIAL_CHAR_ERROR);
      return;
    }

    const duplicate = categories.some(
      (c) => c.name.toLowerCase() === name.toLowerCase() && c.id !== editId
    );
    if (duplicate) {
      toast.error("This category already exists");
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        await db.collection(COLLECTION).doc(editId).update({
          name,
          updated_date: new Date(),
        });
        toast.success("Category updated successfully");
      } else {
        await db.collection(COLLECTION).add({
          name,
          created_date: new Date(),
          updated_date: new Date(),
        });
        toast.success("Category added successfully");
      }
      resetForm();
    } catch (error) {
      console.error("Error saving category: ", error);
      toast.error("Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (cat) => {
    if (!permissible_roles.includes("create_product")) {
      return showNotAuthorised();
    }
    setEditId(cat.id);
    setCategoryName(cat.name);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (cat) => {
    if (!permissible_roles.includes("create_product")) {
      return showNotAuthorised();
    }
    const result = await Swal.fire({
      title: `Delete "${cat.name}"?`,
      text: "Existing products with this category will keep the category name.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      await db.collection(COLLECTION).doc(cat.id).delete();
      toast.success("Category deleted successfully");
      if (editId === cat.id) resetForm();
    } catch (error) {
      console.error("Error deleting category: ", error);
      toast.error("Failed to delete category");
    }
  };

  const showNotAuthorised = () => {
    const Toast = Swal.mixin({
      toast: true,
      background: "#d7e7e6",
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
    });
    Toast.fire({ icon: "error", title: "You are not authorised to do this action" });
  };

  const filtered = categories.filter((c) =>
    c.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(currentPage, totalPages); // stays valid after deletes/search
  const startIndex = (safePage - 1) * pageSize;
  const paginated = filtered.slice(startIndex, startIndex + pageSize);

  // Show at most 5 page numbers around the current page
  const getPageNumbers = () => {
    const maxVisible = 5;
    let start = Math.max(1, safePage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);
    const pages = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  };

  const canCreate = permissible_roles.includes("create_product");

  return (
    <div className="container-scroller">
      <TopPanel />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card" style={{ background: "#4a54ba" }}>
                <div className="card-body">
                  <h4
                    className="card-title"
                    style={{ color: "#fff", fontSize: "20px", fontWeight: "700" }}
                  >
                    Product Categories
                  </h4>

                  {/* Add / Edit form */}
                  <form
                    onSubmit={canCreate ? handleSave : (e) => { e.preventDefault(); showNotAuthorised(); }}
                    style={{
                      display: "flex",
                      gap: "10px",
                      flexWrap: "wrap",
                      background: "#fff",
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "20px",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: "220px" }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Category name (e.g. Fertilizer)"
                        value={categoryName}
                        onChange={handleNameChange}
                      />
                      {errMsg && (
                        <span
                          style={{
                            color: "red",
                            fontSize: "12px",
                            display: "block",
                            marginTop: "4px",
                          }}
                        >
                          {errMsg}
                        </span>
                      )}
                    </div>
                    <button
                      type="submit"
                      className="btn btn-success btn-rounded btn-sm"
                      disabled={saving}
                    >
                      {saving ? "Saving..." : editId ? "Update Category" : "Add Category"}
                    </button>
                    {editId && (
                      <button
                        type="button"
                        className="btn btn-light btn-rounded btn-sm"
                        onClick={resetForm}
                      >
                        Cancel
                      </button>
                    )}
                  </form>

                  {/* List */}
                  <div
                    style={{ background: "#fff", padding: "16px", borderRadius: "8px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px",
                        gap: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <strong>Total: {categories.length}</strong>
                      <input
                        type="text"
                        className="form-control"
                        style={{ maxWidth: "260px" }}
                        placeholder="Search category"
                        value={search}
                        onChange={(e) => {
                          setSearch(e.target.value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>

                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th style={{ width: "60px" }}>#</th>
                            <th>Category</th>
                            <th style={{ width: "180px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="text-center">
                                No categories found
                              </td>
                            </tr>
                          ) : (
                            paginated.map((cat, i) => (
                              <tr key={cat.id}>
                                <td>{startIndex + i + 1}</td>
                                <td>{cat.name}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-info btn-sm mr-2"
                                    onClick={() => handleEdit(cat)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDelete(cat)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {filtered.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: "10px",
                          marginTop: "12px",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ fontSize: "14px" }}>
                            Showing {startIndex + 1}–
                            {Math.min(startIndex + pageSize, filtered.length)} of{" "}
                            {filtered.length}
                          </span>
                          <select
                            className="form-control form-control-sm"
                            style={{ width: "auto" }}
                            value={pageSize}
                            onChange={(e) => {
                              setPageSize(Number(e.target.value));
                              setCurrentPage(1);
                            }}
                          >
                            {[5, 10, 20, 50].map((n) => (
                              <option key={n} value={n}>
                                {n} / page
                              </option>
                            ))}
                          </select>
                        </div>

                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={safePage === 1}
                            onClick={() => setCurrentPage(safePage - 1)}
                          >
                            Prev
                          </button>
                          {getPageNumbers().map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`btn btn-sm ${
                                p === safePage ? "btn-primary" : "btn-outline-primary"
                              }`}
                              onClick={() => setCurrentPage(p)}
                            >
                              {p}
                            </button>
                          ))}
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={safePage === totalPages}
                            onClick={() => setCurrentPage(safePage + 1)}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubProducts;